import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import dashboardService from '../../../services/dashboardService';
import candidateService from '../../../services/candidateService';
import userManagementService from '../../../services/userManagementService';
import Header from '../../../components/ui/Header';
import FixedSidebar from '../../../components/ui/FixedSidebar';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { hasFeatureAccess, hasActionAccess } from '../../../utils/roleBasedAccess';

/**
 * HR Manager Dashboard
 * Comprehensive view for HR Managers with strategic insights and team oversight
 */
const HRManagerDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDashboardData = async () => {
      console.log('[HRManagerDashboard] Loading dashboard data');
      setLoading(true);
      setError(null);
      
      try {
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
        
        // Load dashboard overview
        const overview = await dashboardService.getDashboardOverview();
        setDashboardData(overview);
        
        // Load team-specific stats
        const candidates = await candidateService.getCandidates({ pageSize: 100 });
        setTeamStats(candidates);
        
        // Load user management stats
        try {
          const userStatsData = await userManagementService.getUserStats();
          setUserStats(userStatsData);
        } catch (userError) {
          console.warn('[HRManagerDashboard] User management not available:', userError.message);
          // Continue without user management features
        }
        
      } catch (error) {
        console.error('[HRManagerDashboard] Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const notificationCounts = {
    candidates: teamStats?.data?.filter(c => c.status === 'new').length || 0,
    processing: teamStats?.data?.filter(c => c.status === 'screening').length || 0,
    interviews: teamStats?.data?.filter(c => c.status === 'interview').length || 0,
    total: teamStats?.data?.filter(c => ['new', 'screening', 'interview'].includes(c.status)).length || 0
  };

  const managementMetrics = [
    {
      title: 'Team Performance',
      value: '94%',
      change: '+2%',
      changeType: 'increase',
      icon: 'TrendingUp',
      color: 'success',
      description: 'Overall team efficiency'
    },
    {
      title: 'Time to Hire',
      value: '18 days',
      change: '-3 days',
      changeType: 'decrease',
      icon: 'Clock',
      color: 'primary',
      description: 'Average hiring timeline'
    },
    {
      title: 'Budget Utilization',
      value: '67%',
      change: '+5%',
      changeType: 'increase',
      icon: 'DollarSign',
      color: 'warning',
      description: 'Recruitment budget used'
    },
    {
      title: 'Quality Score',
      value: '4.8/5',
      change: '+0.2',
      changeType: 'increase',
      icon: 'Star',
      color: 'success',
      description: 'Candidate quality rating'
    }
  ];

  // CV Processing Metrics for Manager oversight
  const cvProcessingMetrics = [
    {
      title: 'CVs Processed',
      value: '247',
      change: '+23',
      changeType: 'increase',
      icon: 'Upload',
      color: 'primary',
      description: 'This month'
    },
    {
      title: 'Processing Success Rate',
      value: '96.8%',
      change: '+1.2%',
      changeType: 'increase',
      icon: 'CheckCircle',
      color: 'success',
      description: 'AI analysis completion'
    },
    {
      title: 'Avg Processing Time',
      value: '2.4 min',
      change: '-0.3 min',
      changeType: 'decrease',
      icon: 'Clock',
      color: 'success',
      description: 'Per CV analysis'
    },
    {
      title: 'High-Quality Matches',
      value: '31%',
      change: '+5%',
      changeType: 'increase',
      icon: 'Target',
      color: 'success',
      description: 'Score ≥ 85'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading HR Manager dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/manager" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Dashboard</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} notificationCounts={notificationCounts} />
      <FixedSidebar currentPath="/hr-roles/manager" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Roles', href: '/hr-roles' },
            { label: 'Manager Dashboard', href: '/hr-roles/manager', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">HR Manager Dashboard</h1>
              <p className="text-muted-foreground">
                Strategic overview and team management insights
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* Generate Report - Available to HR Manager */}
              {hasFeatureAccess(currentUser?.role, 'reports') && (
                <Button variant="outline" onClick={() => navigate('/reports')}>
                  Generate Report
                </Button>
              )}
              
              {/* Team Management - Available to HR Manager */}
              {hasActionAccess(currentUser?.role, 'userManagement', 'view-all-users') && (
                <Button onClick={() => navigate('/team-management')}>
                  Manage Team
                </Button>
              )}
            </div>
          </div>

          {/* Management Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {managementMetrics.map((metric, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    metric.color === 'success' ? 'bg-green-100' :
                    metric.color === 'warning' ? 'bg-yellow-100' :
                    metric.color === 'primary' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Icon name={metric.icon} size={24} className={
                      metric.color === 'success' ? 'text-green-600' :
                      metric.color === 'warning' ? 'text-yellow-600' :
                      metric.color === 'primary' ? 'text-blue-600' : 'text-gray-600'
                    } />
                  </div>
                  <span className={`text-sm font-medium ${
                    metric.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">{metric.title}</h3>
                <p className="text-2xl font-bold text-foreground mb-1">{metric.value}</p>
                <p className="text-xs text-muted-foreground">{metric.description}</p>
              </div>
            ))}
          </div>

          {/* CV Processing Metrics */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">CV Processing Overview</h2>
              <Button
                variant="outline"
                onClick={() => navigate('/cv-upload-processing')}
              >
                <Icon name="Upload" size={16} className="mr-2" />
                Manage CV Processing
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
              {cvProcessingMetrics.map((metric, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      metric.color === 'success' ? 'bg-green-100' :
                      metric.color === 'warning' ? 'bg-yellow-100' :
                      metric.color === 'primary' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Icon name={metric.icon} size={24} className={
                        metric.color === 'success' ? 'text-green-600' :
                        metric.color === 'warning' ? 'text-yellow-600' :
                        metric.color === 'primary' ? 'text-blue-600' : 'text-gray-600'
                      } />
                    </div>
                    <span className={`text-sm font-medium ${
                      metric.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {metric.change}
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">{metric.title}</h3>
                  <p className="text-2xl font-bold text-foreground mb-1">{metric.value}</p>
                  <p className="text-xs text-muted-foreground">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Strategic Overview Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            {/* Team Performance Chart */}
            <div className="xl:col-span-2 bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">Team Performance Trends</h3>
                <select className="text-sm border border-border rounded-lg px-3 py-1">
                  <option>Last 30 days</option>
                  <option>Last 3 months</option>
                  <option>Last 6 months</option>
                </select>
              </div>
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Icon name="BarChart3" size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Performance chart will be implemented</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/team-management')}
                >
                  <Icon name="Users" size={16} className="mr-3" />
                  Manage Team
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/reports/generate')}
                >
                  <Icon name="FileText" size={16} className="mr-3" />
                  Generate Report
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/analytics')}
                >
                  <Icon name="TrendingUp" size={16} className="mr-3" />
                  View Analytics
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/budget-management')}
                >
                  <Icon name="DollarSign" size={16} className="mr-3" />
                  Budget Overview
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/cv-upload-processing')}
                >
                  <Icon name="Upload" size={16} className="mr-3" />
                  CV Processing Hub
                </Button>
              </div>
            </div>
          </div>

          {/* CV Processing Analytics & Insights */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">CV Processing Analytics</h2>
              <select className="text-sm border border-border rounded-lg px-3 py-1">
                <option>Last 30 days</option>
                <option>Last 3 months</option>
                <option>Last 6 months</option>
              </select>
            </div>
            
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Processing Trends */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Processing Volume Trends</h3>
                <div className="h-48 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Icon name="TrendingUp" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>CV processing trends chart</p>
                    <p className="text-sm">247 CVs processed (+23 vs last month)</p>
                  </div>
                </div>
              </div>
              
              {/* Top Skills Identified */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Trending Skills Identified</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">React.js</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div className="w-4/5 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">89 CVs</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Python</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div className="w-3/4 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">76 CVs</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">AWS</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div className="w-3/5 h-2 bg-orange-500 rounded-full"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">67 CVs</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">TypeScript</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div className="w-1/2 h-2 bg-purple-500 rounded-full"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">58 CVs</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Docker</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div className="w-2/5 h-2 bg-cyan-500 rounded-full"></div>
                      </div>
                      <span className="text-xs text-muted-foreground">45 CVs</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate('/analytics/skills-trends')}
                  >
                    <Icon name="BarChart3" size={14} className="mr-2" />
                    View Full Skills Analytics
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* User Management Section */}
          {userStats && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Team Management</h2>
                <Button onClick={() => setShowUserManagement(!showUserManagement)}>
                  <Icon name="Users" size={16} className="mr-2" />
                  {showUserManagement ? 'Hide' : 'Manage'} HR Team
                </Button>
              </div>
              
              {/* User Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={24} className="text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Total HR Staff</h3>
                  <p className="text-2xl font-bold text-foreground mb-1">{userStats.total}</p>
                  <p className="text-xs text-muted-foreground">Active team members</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="UserCheck" size={24} className="text-green-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Active Users</h3>
                  <p className="text-2xl font-bold text-foreground mb-1">{userStats.active}</p>
                  <p className="text-xs text-muted-foreground">Currently active</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Icon name="UserPlus" size={24} className="text-purple-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Recent Additions</h3>
                  <p className="text-2xl font-bold text-foreground mb-1">{userStats.recentlyAdded}</p>
                  <p className="text-xs text-muted-foreground">This week</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Icon name="Shield" size={24} className="text-orange-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Recruiters</h3>
                  <p className="text-2xl font-bold text-foreground mb-1">{userStats.byRole?.recruiter || 0}</p>
                  <p className="text-xs text-muted-foreground">Active recruiters</p>
                </div>
              </div>
              
              {/* User Management Panel */}
              {showUserManagement && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-foreground">HR Team Overview</h3>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => navigate('/user-management/add')}
                      >
                        <Icon name="UserPlus" size={16} className="mr-2" />
                        Add User
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate('/user-management')}
                      >
                        <Icon name="Settings" size={16} className="mr-2" />
                        Manage All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Role Distribution */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Icon name="Crown" size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">HR Managers</h4>
                          <p className="text-sm text-muted-foreground">{userStats.byRole?.hr_manager || 0} active</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Strategic oversight and team management</p>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon name="UserPlus" size={16} className="text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">Recruiters</h4>
                          <p className="text-sm text-muted-foreground">{userStats.byRole?.recruiter || 0} active</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Candidate sourcing and interviewing</p>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Icon name="Calendar" size={16} className="text-orange-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">Coordinators</h4>
                          <p className="text-sm text-muted-foreground">{userStats.byRole?.hr_coordinator || 0} active</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Administrative and scheduling support</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent Activities & Alerts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Recent Decisions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">Recent Decisions</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={16} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Approved hire for Senior Developer</p>
                    <p className="text-xs text-muted-foreground">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Icon name="Calendar" size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Scheduled team review meeting</p>
                    <p className="text-xs text-muted-foreground">Yesterday</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Budget threshold alert addressed</p>
                    <p className="text-xs text-muted-foreground">2 days ago</p>
                  </div>
                </div>
              </div>
            </div>

              {/* CV Processing Team Performance */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-6">CV Processing Team Performance</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Sarah Chen - Recruiter</p>
                        <p className="text-xs text-muted-foreground">47 CVs processed this month</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-green-600">94% accuracy</span>
                      <p className="text-xs text-muted-foreground">Avg: 2.1 min/CV</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Mike Rodriguez - Recruiter</p>
                        <p className="text-xs text-muted-foreground">52 CVs processed this month</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-green-600">97% accuracy</span>
                      <p className="text-xs text-muted-foreground">Avg: 1.8 min/CV</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} className="text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Emma Johnson - Coordinator</p>
                        <p className="text-xs text-muted-foreground">31 CVs processed this month</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-yellow-600">89% accuracy</span>
                      <p className="text-xs text-muted-foreground">Avg: 2.7 min/CV</p>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => navigate('/team-management/cv-processing')}
                    >
                      <Icon name="BarChart3" size={14} className="mr-2" />
                      View Detailed Analytics
                    </Button>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRManagerDashboard;